import React from 'react';
import { Container } from 'semantic-ui-react';
import usePostUploadStatus from '@api/upload/hooks/usePostUploadStatus';
import { isInspector } from '@utils/UserRoleSelector';
import useGetUser from '@api/user/hooks/useGetUser';
import UploadStatus from '@constants/uploadStatus';
import ObjectType from '@constants/objectType';
import FileType from '@constants/fileType';
import DownloadableFile from '@components/CustomCell/DownloadableFile';
import DetailsButton from '@components/CustomCell/DetailsButton';
import FileTypeLabel from '@components/CustomCell/FileTypeLabel';
import IsValid from '@components/CustomCell/IsValid';
import ValidationMessage from '@components/CustomCell/ValidationMessage';
import CommentsCount from '@components/CustomCell/CommentsCount';
import SailorLink from '@components/CustomCell/SailorLink';
import RejectionModal from '@components/Upload/RejectionReason/RejectionModal';

export type ColumnName =
  | 'OPTIONS'
  | 'FILE_NAME'
  | 'UPLOAD_TYPE'
  | 'VALID'
  | 'VALIDATION_MESSAGE'
  | 'SAILOR_INSPECTION_ID'
  | 'REFUSE';
type CustomComponent = (...args: any) => React.ReactNode;
type RefreshFunction = (refresh: boolean) => void;

const customCellsDefinition: { [columnName in ColumnName]: CustomComponent } = {
  FILE_NAME: (rowValues: any) => {
    const { ID, FILE_NAME } = rowValues;
    return <DownloadableFile fileName={FILE_NAME as string} uploadId={ID as number} />;
  },
  UPLOAD_TYPE: (rowValues: any) => {
    const { UPLOAD_TYPE } = rowValues;
    return <FileTypeLabel fileType={FileType[UPLOAD_TYPE]} />;
  },
  VALID: (rowValues: any) => {
    const { VALID } = rowValues;
    return <IsValid isValid={VALID} />;
  },
  VALIDATION_MESSAGE: (rowValues: any) => {
    const { VALIDATION_MESSAGE_JSON, VALIDATION_MESSAGE } = rowValues;
    return (
      <ValidationMessage
        validationMessageOld={VALIDATION_MESSAGE}
        validationMessageJson={VALIDATION_MESSAGE_JSON}
      />
    );
  },
  SAILOR_INSPECTION_ID: (rowValues: any) => {
    const {
      ID,
      SAILOR_INSPECTION_ID,
      SAILOR_OBJECT_ID,
      UPLOAD_TYPE,
      COMPANY_OBJECT_TYPE_BACKGROUND,
    } = rowValues;
    if (UPLOAD_TYPE === FileType.REPORT.key) {
      return (
        <SailorLink
          objectType={ObjectType[COMPANY_OBJECT_TYPE_BACKGROUND]}
          inspectionId={SAILOR_INSPECTION_ID}
          objectId={SAILOR_OBJECT_ID}
          inspectionSummaryId={ID}
        />
      );
    }
    return null;
  },
  OPTIONS: (rowValues: any) => {
    const { UPLOAD_TYPE, ID, COMPANY_OBJECT_TYPE_BACKGROUND } = rowValues;
    if (UPLOAD_TYPE === FileType.ACTION_SUMMARY.key) {
      return (
        <DetailsButton
          entityType={ObjectType[COMPANY_OBJECT_TYPE_BACKGROUND]?.urlActionSummarySubString}
          entityId={ID}
          floatingLabel={<CommentsCount uploadId={Number(ID)} />}
        />
      );
    }
    if (UPLOAD_TYPE === FileType.REPORT.key) {
      return (
        <DetailsButton
          entityType="reportDetails"
          entityId={ID}
          floatingLabel={<CommentsCount uploadId={Number(ID)} />}
        />
      );
    }
    return null;
  },
  REFUSE: (rowValues: any, setRefresh: RefreshFunction) => {
    const changeFileSetStatus = usePostUploadStatus(setRefresh);
    const { data: user } = useGetUser();
    const { UPLOAD_TYPE, ID, STATUS } = rowValues;
    if (
      user &&
      isInspector(user) &&
      UPLOAD_TYPE === FileType.REPORT.key &&
      STATUS === UploadStatus.WAITING_FOR_ACCEPTANCE.key
    ) {
      return (
        <Container
          onClick={(e: Event) => {
            e.stopPropagation();
          }}
        >
          <RejectionModal
            basic
            size="tiny"
            rejectFileSet={reason => {
              changeFileSetStatus({ id: ID, status: UploadStatus.REFUSED, reason });
            }}
          />
        </Container>
      );
    }
    return null;
  },
};

export default customCellsDefinition;
